import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  AutoStories as AutoStoriesIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  Save as SaveIcon,
} from "@mui/icons-material";
import api from '../config/api';
import useStore from "../config/store";
import { useTranslation } from "react-i18next";

const emptyNarrativeForm = {
  name: "",
  description: "",
  content: "",
};

const narrativeMaxLengths = {
  name: 50,
  description: 50,
  content: 500,
};

const NarrativesTab = () => {
  const { authToken, organization, project, showAlert } = useStore.getState();
  const { t } = useTranslation();

  const [narratives, setNarratives] = useState([]);
  const [loadingNarratives, setLoadingNarratives] = useState(true);
  const [savingNarrative, setSavingNarrative] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingNarrative, setEditingNarrative] = useState(null);
  const [formData, setFormData] = useState(emptyNarrativeForm);

  const canSave = useMemo(() => {
    return formData.name.trim() !== "" && formData.content.trim() !== "";
  }, [formData]);

  const loadNarratives = async () => {
    try {
      setLoadingNarratives(true);

      if (!organization || !project) {
        setLoadingNarratives(false);
        return;
      }

      const response = await api.get(
        "/projects/gpt-narratives",
        {
          params: {
            organizationId: organization,
            projectId: project,
          },
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );

      if (response.data.response.status_response === "OK") {
        const responseData = response.data.response.data || {};
        setNarratives(responseData.narratives || []);
      }
    } catch (err) {
      console.error("Error cargando narrativas GPT:", err);
      showAlert(t("narrativesConfig.messages.loadError"), "error");
    } finally {
      setLoadingNarratives(false);
    }
  };

  const handleOpenCreate = () => {
    setEditingNarrative(null);
    setFormData(emptyNarrativeForm);
    setOpenDialog(true);
  };

  const handleOpenEdit = (narrative) => {
    setEditingNarrative(narrative);
    setFormData({
      name: narrative.name || "",
      description: narrative.description || "",
      content: narrative.content || "",
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    if (savingNarrative) return;

    setOpenDialog(false);
    setEditingNarrative(null);
    setFormData(emptyNarrativeForm);
  };

  const handleChange = (field, value) => {
    const maxLength = narrativeMaxLengths[field];

    if (maxLength && value.length > maxLength) {
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSaveNarrative = async () => {
    try {
      if (!canSave) {
        showAlert(t("narrativesConfig.messages.requiredFields"), "error");
        return;
      }

      if (!organization || !project) {
        showAlert(t("narrativesConfig.messages.cannotSave"), "error");
        return;
      }

      setSavingNarrative(true);

      const payload = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        content: formData.content.trim(),
      };

      if (editingNarrative?.id) {
        const response = await api.put(
          `/projects/gpt-narratives/${editingNarrative.id}`,
          payload,
          {
            params: {
              organizationId: organization,
              projectId: project,
            },
            headers: { Authorization: `Bearer ${authToken}` },
          }
        );

        if (response.data.response.status_response === "OK") {
          showAlert(t("narrativesConfig.messages.updateSuccess"), "success");
          handleCloseDialog();
          await loadNarratives();
        } else {
          showAlert(t("narrativesConfig.messages.updateError"), "error");
        }
      } else {
        const response = await api.post(
          "/projects/gpt-narratives",
          payload,
          {
            params: {
              organizationId: organization,
              projectId: project,
            },
            headers: { Authorization: `Bearer ${authToken}` },
          }
        );

        if (response.data.response.status_response === "OK") {
          showAlert(t("narrativesConfig.messages.createSuccess"), "success");
          handleCloseDialog();
          await loadNarratives();
        } else {
          showAlert(t("narrativesConfig.messages.createError"), "error");
        }
      }
    } catch (err) {
      console.error("Error guardando narrativa GPT:", err);

      const backendMessage = err?.response?.data?.response?.message;

      if (backendMessage) {
        showAlert(backendMessage, "error");
      } else {
        showAlert(t("narrativesConfig.messages.saveError"), "error");
      }
    } finally {
      setSavingNarrative(false);
    }
  };

  useEffect(() => {
    loadNarratives();
  }, [organization, project]);

  return (
    <Box>
      {loadingNarratives && (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 4 }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>
            {t("narrativesConfig.loading")}
          </Typography>
        </Box>
      )}

      {!loadingNarratives && (
        <>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <AutoStoriesIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6" sx={{ flexGrow: 1 }}>
                {t("narrativesConfig.title")}
              </Typography>

              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleOpenCreate}
              >
                {t("narrativesConfig.create")}
              </Button>
            </Box>

            <Typography variant="body1" color="text.secondary" paragraph>
              {t("narrativesConfig.description")}
            </Typography>

            <Alert severity="info">
              {t("narrativesConfig.info")}
            </Alert>
          </Paper>

          {narratives.length === 0 && (
            <Alert severity="warning">
              {t("narrativesConfig.noAvailable")}
            </Alert>
          )}

          {narratives.length > 0 && (
            <Grid container spacing={3}>
              {narratives.map((narrative) => (
                <Grid item xs={12} md={6} key={narrative.id}>
                  <Card sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Box sx={{ display: "flex", alignItems: "flex-start", mb: 1 }}>
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="h6">
                            {narrative.name}
                          </Typography>
                        </Box>

                        <Tooltip title={t("narrativesConfig.editTooltip")}>
                          <IconButton onClick={() => handleOpenEdit(narrative)}>
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>

                      {narrative.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {narrative.description}
                        </Typography>
                      )}

                      <Divider sx={{ my: 2 }} />

                      <Typography
                        variant="body2"
                        sx={{
                          whiteSpace: "pre-line",
                          maxHeight: 180,
                          overflowY: "auto",
                        }}
                      >
                        {narrative.content}
                      </Typography>
                    </CardContent>

                    <CardActions sx={{ justifyContent: "flex-end", px: 2, pb: 2 }}>
                      <Button
                        variant="outlined"
                        startIcon={<EditIcon />}
                        onClick={() => handleOpenEdit(narrative)}
                      >
                        {t("narrativesConfig.edit")}
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}

          <Dialog
            open={openDialog}
            onClose={handleCloseDialog}
            fullWidth
            maxWidth="md"
          >
            <DialogTitle sx={{ display: "flex", alignItems: "center" }}>
              <AutoStoriesIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6" sx={{ flexGrow: 1 }}>
                {editingNarrative ? t("narrativesConfig.editTitle") : t("narrativesConfig.createTitle")}
              </Typography>

              <IconButton onClick={handleCloseDialog} disabled={savingNarrative}>
                <CloseIcon />
              </IconButton>
            </DialogTitle>

            <DialogContent dividers>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={t("narrativesConfig.fields.name.label")}
                    value={formData.name}
                    onChange={(e) => handleChange("name", e.target.value)}
                    placeholder={t("narrativesConfig.fields.name.placeholder")}
                    inputProps={{ maxLength: narrativeMaxLengths.name }}
                    helperText={`${formData.name.length}/${narrativeMaxLengths.name}`}
                    required
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={t("narrativesConfig.fields.description.label")}
                    value={formData.description}
                    onChange={(e) => handleChange("description", e.target.value)}
                    placeholder={t("narrativesConfig.fields.description.placeholder")}
                    inputProps={{ maxLength: narrativeMaxLengths.description }}
                    helperText={`${formData.description.length}/${narrativeMaxLengths.description}`}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={6}
                    label={t("narrativesConfig.fields.content.label")}
                    value={formData.content}
                    onChange={(e) => handleChange("content", e.target.value)}
                    placeholder={t("narrativesConfig.fields.content.placeholder")}
                    inputProps={{ maxLength: narrativeMaxLengths.content }}
                    helperText={`${formData.content.length}/${narrativeMaxLengths.content}`}
                    required
                  />
                </Grid>
              </Grid>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button
                onClick={handleCloseDialog}
                disabled={savingNarrative}
              >
                {t("narrativesConfig.cancel")}
              </Button>

              <Button
                variant="contained"
                onClick={handleSaveNarrative}
                disabled={savingNarrative || !canSave}
                startIcon={savingNarrative ? <CircularProgress size={20} /> : <SaveIcon />}
              >
                {savingNarrative ? t("saving") : t("narrativesConfig.saveNarrative")}
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Box>
  );
};

export default NarrativesTab;